import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/api';
import CreditNoteForm from '../components/creditNotes/CreditNoteForm';
import { showConfirm } from '../utils/confirmUtils';
import { toastSuccess, toastError } from '../utils/toasts';
import { Button, Badge } from '../components/ui';
import ExportModal from '../components/ExportModal';
import { IconRefresh, IconPlus, IconEye, IconEdit, IconTrash, IconArrowBackUp, IconChevronLeft, IconChevronRight, IconFileExport } from '@tabler/icons-react';

const normalizeList = (data) => Array.isArray(data) ? data : (data?.items || data?.data || []);
const toNumber = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

const emptyForm = {
  clientId: '', creditNoteDate: new Date().toISOString().split('T')[0],
  linkedInvoiceId: '', status: 'DRAFT', timbreFiscal: 0,
  items: [{ description: '', quantity: 1, priceHT: 0, tvaRate: 19, itemType: 'honoraire' }],
  notes: '',
};

const STATUS_BADGE = {
  DRAFT: 'gray', VALIDATED: 'info', APPLIED: 'success', CANCELLED: 'danger',
};

const STATUS_LABELS = {
  DRAFT: 'Brouillon', VALIDATED: 'Validé', APPLIED: 'Appliqué', CANCELLED: 'Annulé',
};

export default function CreditNotes() {
  const [creditNotes, setCreditNotes] = useState([]);
  const [clients, setClients]         = useState([]);
  const [invoices, setInvoices]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [formError, setFormError]     = useState('');
  const [search, setSearch]           = useState('');
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  const [showModal, setShowModal]               = useState(false);
  const [editingCreditNote, setEditingCreditNote] = useState(null);
  const [form, setForm]                           = useState(emptyForm);
  const [showExportModal, setShowExportModal] = useState(false);

  const creditNoteColumns = [
    { key: 'creditNoteNumber', label: 'N° Avoir', accessor: (cn) => cn?.creditNoteNumber || '—' },
    { key: 'client', label: 'Client', accessor: (cn) => cn?.clientName || cn?.clientId?.companyName || cn?.clientSnapshot?.name || '—' },
    { key: 'linkedInvoiceNumber', label: 'Facture liée', accessor: (cn) => cn?.linkedInvoiceNumber || '—' },
    { key: 'creditNoteDate', label: 'Date', accessor: (cn) => cn?.creditNoteDate ? cn.creditNoteDate.split('T')[0] : '—' },
    { key: 'grandTotal', label: 'Montant TTC', accessor: (cn) => `${toNumber(cn?.grandTotal).toFixed(3)} DT` },
    { key: 'status', label: 'Statut', accessor: (cn) => STATUS_LABELS[cn?.status] || cn?.status || '—' },
  ];

  const setField = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handlePreviewPDF = async (cn) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('token');
      const workspaceId = localStorage.getItem('active_workspace_id') || '';
      const response = await fetch(`${apiUrl}/api/credit-notes/${cn._id}/pdf`, {
        headers: { Authorization: `Bearer ${token}`, 'x-workspace-id': workspaceId }
      });
      if (!response.ok) throw new Error('Erreur génération PDF');
      const blob = await response.blob();
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (e) { toastError('Erreur lors de la génération du PDF : ' + e.message); }
  };

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [cnRes, cRes, invRes] = await Promise.all([
        api.get('/credit-notes'), api.get('/clients'), api.get('/invoices')
      ]);
      setCreditNotes(normalizeList(cnRes.data));
      setClients(normalizeList(cRes.data));
      setInvoices(normalizeList(invRes.data));
    } catch (e) { setError(e?.response?.data?.error || 'Erreur chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return creditNotes;
    return creditNotes.filter(cn =>
      (cn?.creditNoteNumber || '').toLowerCase().includes(q) ||
      (cn?.clientName || '').toLowerCase().includes(q) ||
      (cn?.clientId?.companyName || '').toLowerCase().includes(q) ||
      (cn?.clientSnapshot?.name || '').toLowerCase().includes(q) ||
      (cn?.status || '').toLowerCase().includes(q) ||
      (cn?.linkedInvoiceNumber || '').toLowerCase().includes(q)
    );
  }, [creditNotes, search]);

  useEffect(() => { setPage(1); }, [creditNotes.length]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openCreate = () => { setEditingCreditNote(null); setForm(emptyForm); setFormError(''); setShowModal(true); };
  const openEdit   = (cn) => {
    setEditingCreditNote(cn);
    setForm({
      clientId:        cn.clientId?._id || cn.clientId || '',
      creditNoteDate:  cn.creditNoteDate ? cn.creditNoteDate.split('T')[0] : '',
      linkedInvoiceId: cn.linkedInvoiceId || '',
      status:          cn.status || 'DRAFT',
      timbreFiscal:    cn.timbreFiscal ?? 0,
      items:           cn.items?.length ? cn.items : emptyForm.items,
      notes:           cn.notes || '',
    });
    setFormError(''); setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditingCreditNote(null); setForm(emptyForm); setFormError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setFormError('');
    try {
      const payload = { ...form, timbreFiscal: Number(form.timbreFiscal) || 0 };
      if (editingCreditNote) await api.put('/credit-notes/' + (editingCreditNote._id || editingCreditNote.id), payload);
      else                   await api.post('/credit-notes', payload);
      closeModal(); await load();
    } catch (e) { setFormError(e?.response?.data?.error || 'Erreur enregistrement'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (cn) => {
    if (!await showConfirm('Supprimer cet avoir ?')) return;
    try { await api.delete('/credit-notes/' + (cn._id || cn.id)); await load(); }
    catch (e) { setError(e?.response?.data?.error || 'Erreur suppression'); }
  };

  return (
    <div className="flex-1 p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-1">Avoirs</h1>
        <p className="text-gray-500 text-sm">Gérez vos avoirs et notes de crédit clients.</p>
      </div>

      <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-800">Liste des avoirs</h3>
          <p className="text-sm text-gray-500">{filtered.length} avoir(s)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => setShowExportModal(true)} disabled={filtered.length === 0}>
            <IconFileExport size={15} strokeWidth={1.6} className="mr-1" />
            Export
          </Button>
          <Button variant="secondary" size="sm" onClick={load} disabled={loading}>
            <IconRefresh size={15} strokeWidth={1.6} />
            Rafraîchir
          </Button>
          <Button variant="primary" size="sm" onClick={openCreate}>
            <IconPlus size={15} strokeWidth={1.6} />
            Nouvel avoir
          </Button>
        </div>
      </div>

      {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>}

      <div className="bg-white rounded-xl border border-gray-200 mb-6 p-4">
        <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="Rechercher par numéro, client, facture liée, statut..." />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
            <p className="mt-3 text-gray-500 text-sm">Chargement...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
              <IconArrowBackUp size={32} strokeWidth={1.3} className="text-gray-400" />
            </div>
            <h3 className="text-base font-medium text-gray-700 mb-2">Aucun avoir</h3>
            <p className="text-gray-500 mb-6 text-sm">Créez votre premier avoir client.</p>
            <Button variant="primary" size="sm" onClick={openCreate}>
              <IconPlus size={15} strokeWidth={1.6} />
              Nouvel avoir
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['N° Avoir', 'Client', 'Facture liée', 'Date', 'Montant TTC', 'Statut', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginated.map((cn) => (
                  <tr key={cn._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-gray-800 font-semibold">{cn.creditNoteNumber || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{cn.clientName || cn.clientId?.companyName || cn.clientSnapshot?.name || '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-600">{cn.linkedInvoiceNumber || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{cn.creditNoteDate ? cn.creditNoteDate.split('T')[0] : '—'}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-purple-700">- {toNumber(cn.grandTotal).toFixed(3)} DT</td>
                    <td className="px-4 py-3">
                      <Badge variant={STATUS_BADGE[cn.status] || 'gray'}>
                        {STATUS_LABELS[cn.status] || cn.status || '—'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => handlePreviewPDF(cn)} title="Voir PDF" type="button"
                          className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 border border-blue-200 transition-colors">
                          <IconEye size={14} strokeWidth={1.6} />
                        </button>
                        <button onClick={() => openEdit(cn)} title="Modifier" type="button"
                          className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50 border border-amber-200 transition-colors">
                          <IconEdit size={14} strokeWidth={1.6} />
                        </button>
                        <button onClick={() => handleDelete(cn)} title="Supprimer" type="button"
                          className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 border border-red-200 transition-colors">
                          <IconTrash size={14} strokeWidth={1.6} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">
              {filtered.length} avoir(s) — Page {page}/{totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <IconChevronLeft size={16} strokeWidth={1.6} />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const p = start + i;
                if (p > totalPages) return null;
                return (
                  <button key={p} onClick={() => setPage(p)}
                    className={`min-w-[32px] px-2 py-1 text-xs font-medium rounded-lg border transition-colors ${p === page ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 text-gray-600 hover:bg-white'}`}>
                    {p}
                  </button>
                );
              })}
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <IconChevronRight size={16} strokeWidth={1.6} />
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <CreditNoteForm form={form} setField={setField} clients={clients} invoices={invoices}
          editingCreditNote={editingCreditNote} onSubmit={handleSubmit} onClose={closeModal} loading={loading} formError={formError} />
      )}

      {showExportModal && (
        <ExportModal
          columns={creditNoteColumns}
          data={filtered}
          title="Liste des avoirs"
          filename="avoirs"
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
}
